import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTeams, updateTeamPlayers } from '../../store/slices/teamSlice';
import { fetchTournamentDetails } from '../../store/slices/tournamentSlice';
import { useParams, Link } from 'react-router-dom';
import {
    ChevronLeft, Users, CheckCircle, Clock, ShieldCheck, Mail, Save, User as UserIcon
} from 'lucide-react';

const AdminTournamentTeams = () => {
    const { slug, id } = useParams();
    const dispatch = useDispatch();

    const { teams, loading: loadingTeams } = useSelector(state => state.teams);
    const { tournamentDetails, loading: loadingTourn } = useSelector(state => state.tournaments);

    const [selectedTeam, setSelectedTeam] = useState(null);
    const [editingPlayers, setEditingPlayers] = useState([]);
    const [savingPlayers, setSavingPlayers] = useState(false);

    useEffect(() => {
        dispatch(fetchTournamentDetails({ slug, id }));
        dispatch(fetchTeams(id));
    }, [id, slug, dispatch]);

    const handleOpenTeam = (team) => {
        setSelectedTeam(team);
        // Initialize empty slots if needed (e.g., minimum 5 players)
        const existing = team.players || [];
        setEditingPlayers([...existing]);
    };

    const handlePlayerChange = (index, field, value) => {
        const next = [...editingPlayers];
        if (!next[index]) next[index] = { name: '', dni: '', email: '', phone: '' };
        next[index][field] = value;
        setEditingPlayers(next);
    };

    const addPlayerRow = () => {
        setEditingPlayers([...editingPlayers, { name: '', dni: '', email: '', phone: '' }]);
    };

    const savePlayers = async () => {
        setSavingPlayers(true);
        // Filter out empty rows
        const validPlayers = editingPlayers.filter(p => p.name.trim() && p.dni.trim());
        try {
            await dispatch(updateTeamPlayers({ teamId: selectedTeam._id, players: validPlayers })).unwrap();
            alert('Lista de buena fe actualizada correctamente.');
            setSelectedTeam(null);
        } catch (err) {
            alert(err.error || 'Error al guardar');
        } finally {
            setSavingPlayers(false);
        }
    };

    if (loadingTeams || loadingTourn) {
        return (
            <div className="min-h-screen bg-[#080808] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#080808] text-white pb-20 overflow-x-hidden">
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px] animate-glow" />
            </div>

            <div className="max-w-6xl mx-auto px-6 pt-12 relative z-10">
                <Link
                    to={`/${slug}/admin/tournaments`}
                    className="flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-8 group w-fit"
                >
                    <ChevronLeft size={20} className="transition-transform group-hover:-translate-x-1" />
                    <span className="font-black text-[10px] uppercase tracking-[2px]">Volver a Torneos</span>
                </Link>

                {tournamentDetails && (
                    <div className="mb-12">
                        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-2">Equipos Inscriptos</h1>
                        <p className="text-white/40 font-medium text-lg">
                            {tournamentDetails.name} <span className="mx-2">•</span> {teams.length} / {tournamentDetails.maxTeams} cupos
                        </p>
                    </div>
                )}

                {teams.length === 0 ? (
                    <div className="bg-glass p-20 rounded-[40px] text-center border border-white/5 shadow-3xl">
                        <Users size={64} className="mx-auto text-white/10 mb-6" />
                        <p className="text-xl font-bold text-white/40">Todavía no hay equipos inscriptos.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {teams.map(team => (
                            <div key={team._id} className="bg-glass rounded-[32px] p-6 shadow-3xl border border-white/5 hover:border-white/20 transition-all flex flex-col group shine-effect">
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        <h3 className="text-2xl font-black text-white group-hover:text-blue-400 transition-colors uppercase truncate" title={team.name}>{team.name}</h3>
                                        <p className="text-[10px] font-black uppercase text-white/30 tracking-widest mt-1 flex items-center gap-1">
                                            <ShieldCheck size={12} className="text-blue-500" />
                                            Capitán: {team.captainId?.nombre || 'Desconocido'}
                                        </p>
                                    </div>
                                    {team.paymentStatus === 'approved' ? (
                                        <CheckCircle size={24} className="text-green-500 shrink-0 shadow-lg shadow-green-500/20 rounded-full" />
                                    ) : (
                                        <Clock size={24} className="text-yellow-500 shrink-0" />
                                    )}
                                </div>

                                <div className="space-y-3 mb-6 flex-1">
                                    <div className="flex justify-between text-sm py-2 border-b border-white/5">
                                        <span className="text-white/40 font-medium">Jugadores cargados</span>
                                        <span className="font-black">{team.players?.length || 0}</span>
                                    </div>
                                    <div className="flex justify-between text-sm py-2 border-b border-white/5">
                                        <span className="text-white/40 font-medium">Estado pago</span>
                                        <span className={`font-black uppercase tracking-widest text-[10px] ${team.paymentStatus === 'approved' ? 'text-green-400' : 'text-yellow-400'
                                            }`}>
                                            {team.paymentStatus}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleOpenTeam(team)}
                                    className="w-full py-3 bg-blue-500/10 text-blue-400 font-black text-xs uppercase tracking-widest rounded-xl hover:bg-blue-500 hover:text-white transition-all border border-blue-500/20"
                                >
                                    Ver Lista de Buena Fe
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Editor Modal */}
            {selectedTeam && (
                <div className="fixed inset-0 z-50 flex justify-end bg-black/80 backdrop-blur-sm animate-in fade-in">
                    <div className="w-full max-w-xl h-full bg-[#0d0d0d] border-l border-white/10 p-8 overflow-y-auto animate-in slide-in-from-right-full duration-300">
                        <div className="flex justify-between items-center mb-8 pb-6 border-b border-white/5">
                            <div>
                                <p className="text-[10px] font-black text-blue-500 uppercase tracking-[2px] mb-1">Lista de Jugadores</p>
                                <h2 className="text-3xl font-black text-white">{selectedTeam.name}</h2>
                            </div>
                            <button onClick={() => setSelectedTeam(null)} className="p-2 bg-white/5 rounded-full hover:bg-white/10 text-white/50">
                                <ChevronLeft size={20} className="rotate-180" />
                            </button>
                        </div>

                        <div className="space-y-4 mb-8">
                            {editingPlayers.map((player, idx) => (
                                <div key={idx} className="bg-black/40 border border-white/5 rounded-2xl p-4 space-y-4 relative group">
                                    <div className="absolute -left-3 -top-3 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-black text-xs border-4 border-[#0d0d0d]">
                                        {idx + 1}
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 pt-2">
                                        <input
                                            type="text"
                                            placeholder="Nombre Completo"
                                            value={player.name}
                                            onChange={(e) => handlePlayerChange(idx, 'name', e.target.value)}
                                            className="bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-white/20 outline-none focus:border-blue-500/50"
                                        />
                                        <input
                                            type="text"
                                            placeholder="DNI"
                                            value={player.dni}
                                            onChange={(e) => handlePlayerChange(idx, 'dni', e.target.value)}
                                            className="bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-white/20 outline-none focus:border-blue-500/50"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={addPlayerRow}
                            className="w-full py-4 border-2 border-dashed border-white/10 rounded-2xl text-white/40 font-bold hover:text-white hover:border-white/30 transition-colors mb-8 text-sm"
                        >
                            + AGREGAR OTRO JUGADOR
                        </button>

                        <button
                            onClick={savePlayers}
                            disabled={savingPlayers}
                            className="w-full bg-blue-600 text-white font-black py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/20 disabled:opacity-50"
                        >
                            {savingPlayers ? 'GUARDANDO...' : <><Save size={20} /> GUARDAR LISTA</>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTournamentTeams;
